import {useAppSelector} from 'src/app/hooks';
import {UserTypeEnum} from 'src/enums';
import {MePermissionsDTO} from 'src/types';

/**
 * The questions the UI actually asks, answered without naming a role.
 *
 * The code these replace compared role names in about a hundred files, which
 * conflated two different questions. "Which variant of this screen" is about who someone is and is
 * answered by userType; "may they do this" is about permissions and is answered by the catalog. Mixing
 * them is why the same check appeared in three spellings, and why a business-defined role name would
 * have made every one of them silently false.
 */

/** May the caller do this? Names a catalog permission, e.g. `chargers.reset`. */
export const useCan = (permission: string): boolean => {
    const access = useAppSelector((state) => state.common.access);
    return !!access?.permissions.includes(permission);
};

/** May the caller do any of these? For a control that covers several endpoints. */
export const useCanAny = (...permissions: string[]): boolean => {
    const access = useAppSelector((state) => state.common.access);
    return permissions.some((permission) => access?.permissions.includes(permission));
};

/**
 * May the caller do all of these? For one control whose endpoint requires several permissions.
 *
 * A helper rather than `useCan(a) && useCan(b)`, because that is two hooks behind a short circuit: when
 * the first is false the second never runs, the hook count changes between renders, and React throws
 * "Rendered more hooks than during the previous render" the moment the permissions arrive and flip it.
 */
export const useCanAll = (...permissions: string[]): boolean => {
    const access = useAppSelector((state) => state.common.access);
    return permissions.every((permission) => !!access?.permissions.includes(permission));
};

/**
 * May the caller open this page? Takes what {@link PAGE_PERMISSION} holds, which is one permission or
 * several — a page reachable by more than one route needs any one of them.
 */
export const useCanOpen = (permission: string | readonly string[]): boolean => {
    const access = useAppSelector((state) => state.common.access);
    const required = Array.isArray(permission) ? permission : [permission as string];
    return required.some((name) => !!access?.permissions.includes(name));
};

export interface Persona {
    /** Sees every tenant. Replaces `role === SUPER_ADMIN`, which was always a scope question. */
    isPlatformAdmin: boolean;
    /** A company's own account. */
    isCompany: boolean;
    /** Someone working for a company. */
    isEmployee: boolean;
    /** A private EV driver. */
    isDriver: boolean;
    /** The business owner specifically, as opposed to their staff. */
    isBusinessOwner: boolean;
    /** Owner or back-office staff of a business. */
    isBackOffice: boolean;
    /** Only their own properties. */
    isRealEstate: boolean;
}

export const usePersona = (): Persona => {
    const access = useAppSelector((state) => state.common.access);
    return personaOf(access);
};

/** For the handful of places that run outside a component and cannot use a hook. */
/**
 * The business's own people: the owner and the staff acting for them.
 *
 * A list rather than one value because BUSINESS_MANAGER was split into the four jobs it stood for -
 * one value that four different people arrived as, which nothing downstream could tell apart.
 */
export const BACK_OFFICE_TYPES: readonly string[] = [
    UserTypeEnum.SUPER_ADMIN,
    UserTypeEnum.BUSINESS,
    UserTypeEnum.CLIENT_MANAGER,
    UserTypeEnum.SALES_MANAGER,
    UserTypeEnum.TECHNICAL_MANAGER,
    UserTypeEnum.REAL_ESTATE,
];

export const personaOf = (access: MePermissionsDTO | null | undefined): Persona => {
    const userType = access?.userType;
    const scope = access?.scope;
    const scopeType = scope?.type;
    return {
        isPlatformAdmin: scopeType === 'GLOBAL',
        isCompany: userType === UserTypeEnum.COMPANY,
        isEmployee: userType === UserTypeEnum.EMPLOYEE,
        isDriver: userType === UserTypeEnum.PRIVATE_DRIVER,
        isBusinessOwner: userType === UserTypeEnum.BUSINESS,
        isBackOffice: !!userType && BACK_OFFICE_TYPES.includes(userType),
        isRealEstate: scopeType === 'REAL_ESTATE',
    };
};

/** The caller's data slice, for the few screens that legitimately need the ids. */
export const useScope = () => useAppSelector((state) => state.common.access?.scope);
